// apps/share-web/src/ui/erase-offline-button.ts
//
// «Стереть офлайн-данные» — единственный орган, которым человек управляет
// кэшем сам. Срока годности у кэша нет (решение Сергея 21.09), и после ревью
// качества 401 его тоже не стирает: один отказ прокси посреди служения не
// имеет права обнулить библиотеку. Поэтому эта кнопка и выход из аккаунта —
// ДВА ЕДИНСТВЕННЫХ пути стирания во всём продукте.
//
// Шаг подтверждения повторяет приём волны F (ui/song-sheet-words.ts,
// `words-remove`): необратимое действие не случается с одного касания, а
// подтверждение живёт на месте кнопки, а не в отдельном диалоге — на телефоне
// диалог перекрывает то, о чём спрашивает.
//
// Фокус после подтверждения встаёт на ОТКАЗ, а не на «стереть»: человек,
// нажавший Enter и не успевший прочитать вопрос, вторым Enter отменяет, а не
// доводит необратимое до конца.
//
// Область исхода — ПОСТОЯННАЯ и переживает перерисовки ряда. Живой регион,
// вставленный в дерево уже с текстом, скринридер не объявляет: объявляется
// ИЗМЕНЕНИЕ внутри региона, который там уже был. Пустой span с самого начала
// — единственный способ, чтобы «стёрто» прозвучало.

import { t } from './i18n.js';

export interface EraseOfflineOpts {
  erase: () => Promise<void>;
}

export function buildEraseOfflineButton(opts: EraseOfflineOpts): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'lib-erase-offline';

  const row = document.createElement('div');
  row.className = 'lib-erase-row';

  const done = document.createElement('span');
  done.dataset.role = 'erase-offline-done';
  done.className = 'lib-erase-done';
  // polite, не assertive: человек сам только что нажал, и перебивать чтение
  // страницы ради подтверждения собственного действия незачем.
  done.setAttribute('aria-live', 'polite');

  wrap.appendChild(row);
  wrap.appendChild(done);

  const paintIdle = (): void => {
    row.textContent = '';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.dataset.role = 'erase-offline';
    btn.className = 'lib-erase-btn';
    btn.textContent = t('library.eraseOffline');
    btn.addEventListener('click', paintConfirm);
    row.appendChild(btn);
  };

  const paintConfirm = (): void => {
    row.textContent = '';
    done.textContent = '';

    const no = document.createElement('button');
    no.type = 'button';
    no.dataset.role = 'erase-offline-no';
    no.className = 'lib-erase-no';
    no.textContent = t('library.eraseOfflineCancel');
    no.addEventListener('click', paintIdle);

    const yes = document.createElement('button');
    yes.type = 'button';
    yes.dataset.role = 'erase-offline-yes';
    yes.className = 'lib-erase-yes';
    yes.textContent = t('library.eraseOfflineConfirm');
    yes.addEventListener('click', () => { void run(yes); });

    // Отказ ПЕРВЫМ и в разметке: порядок обхода Tab совпадает с порядком
    // чтения, и первым под рукой оказывается безопасный выбор.
    row.appendChild(no);
    row.appendChild(yes);
    no.focus();
  };

  const run = async (yes: HTMLButtonElement): Promise<void> => {
    yes.disabled = true;
    // Отказ хранилища глотается: сказать «стёрто» честнее, чем оставить
    // человека перед кнопкой, которая на вид ничего не сделала, — проверить
    // остаток ему всё равно нечем. Настоящая защита от несделанного стирания
    // в другом: выход из аккаунта зовёт то же стирание сам.
    await opts.erase().catch(() => {});
    row.textContent = '';
    done.textContent = t('library.eraseOfflineDone');
  };

  paintIdle();
  return wrap;
}
